import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from "react";
import { useActiveGithubOrgForUserNonSuspense, useConnectedGithubOrgsNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useDismissableSidebarPopover } from "@/hooks/useDismissableSidebarPopover";
import { useGlobalNetworkMonitoringEnabled } from "@/hooks/useGlobalNetworkMonitoringEnabled";
import { GaugeCircleIcon, ExternalLink, DatabaseZap, X, HardDrive, CirclePlay, Terminal, Workflow, Activity, History, Layers, Network, Ruler, Wand } from "lucide-react";
import { LiDockerIcon } from "@/components/icons/LiDockerIcon";
import { useQueryClient } from "react-query";
import { useApi } from "@/hooks/useApi";
import { fetchLogSearch, fetchLogSearchHistogram } from "@/hooks/useLogSearch";
import { useAtom } from "jotai";
import { recentStartDateAtom, recentEndDateAtom } from "@/atoms/runsFilterAtoms";
import LazyMigrationWizard from "../LazyMigrationWizard";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { cn } from "@repo/blacksmith-ui/cn";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { useCodesmithSidebarVariant } from "@/components/codesmith/useCachedSidebarRedesignEnabled";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/blacksmith-ui/popover";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { type NavSubItem, SidebarHoverCardMenuItem } from "./SidebarHoverCardMenuItem";
import type { NamedOrg } from "@/lib/named-org";
function MigrationWizardPopover(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const {
    activeGithubOrgHasRunFirstJob
  } = useActiveGithubOrgForUserNonSuspense();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const shouldShow = connectedGithubOrgs !== undefined && activeGithubOrgHasRunFirstJob === false;
  const {
    open,
    handleClose,
    isCollapsed,
    shouldRender
  } = useDismissableSidebarPopover("migration-wizard-popover-dismissed", shouldShow);
  if (!shouldRender) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <PopoverTrigger asChild={true}>{children}</PopoverTrigger>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isCollapsed ? "transition-none" : "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=right]:slide-in-from-left-2 duration-200";
  let t3;
  if ($[4] !== t2) {
    t3 = cn("w-auto overflow-visible p-0 ring-0", "rounded-xl border border-black/15 bg-blacksmith-yellow shadow-sm dark:border-black/15 dark:bg-blacksmith-yellow", t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X size={12} strokeWidth={2.5} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== handleClose) {
    t5 = <button type="button" onClick={handleClose} aria-label="Dismiss migration wizard tip" className="absolute right-2 top-2 z-10 rounded-full p-0.5 text-neutral-950 transition-colors hover:bg-black/10">{t4}</button>;
    $[7] = handleClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex flex-1 items-center gap-1.5 pr-1"><Wand className="size-6 shrink-0 text-neutral-950" aria-hidden={true} /><p className="type-body-xs-regular m-0 whitespace-pre-wrap text-neutral-950">Create a PR to migrate{"\n"}your workflows</p></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <svg className="pointer-events-none absolute left-[-6px] top-1/2 -translate-y-1/2" width={7} height={14} viewBox="0 0 7 14" aria-hidden={true}><path d="M7 0 L0 7 L7 14 Z" fill="var(--blacksmith-yellow)" /><path d="M6.43 0.57 L0 7 L6.43 13.43" fill="none" strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" className="stroke-black/15" /></svg>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5) {
    t8 = <div className="relative flex min-h-[32px] items-center py-2 pl-3 pr-8">{t5}{t6}{t7}</div>;
    $[11] = t5;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <PopoverContent side="right" align="center" sideOffset={8} className={t3}>{t8}</PopoverContent>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== open || $[17] !== t1 || $[18] !== t9) {
    t10 = <Popover open={open} onOpenChange={_temp}>{t1}{t9}</Popover>;
    $[16] = open;
    $[17] = t1;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}

/**
 * Contextual sidebar for the GitHub Actions app. Sits to the right of the
 * app-switcher rail; app switching, org selection, notifications, support,
 * and settings live in the rail (AppSwitcherRail.tsx).
 */
function _temp() {}
export default function Sidebar() {
  const $ = _c(172);
  const router = useRouter();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const globalNetworkMonitoringEnabled = useGlobalNetworkMonitoringEnabled();
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  const currentOrg = urlOrg || activeNamedOrg?.orgName;
  const redesign = useCodesmithSidebarVariant(activeNamedOrg) === "redesign";
  const navGapClass = redesign ? "gap-0" : "gap-1";
  const rowFillInsetClass = redesign && "group-data-[state=expanded]:border-y-2 group-data-[state=expanded]:bg-clip-padding";
  const prefetchOnIntent = !!currentOrg;
  const orgRoot = currentOrg ? `/${currentOrg}` : null;
  let t0;
  if ($[0] !== orgRoot) {
    t0 = path => orgRoot ? `${orgRoot}${path}` : path;
    $[0] = orgRoot;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const orgHref = t0;
  let t1;
  if ($[2] !== orgRoot || $[3] !== router.asPath) {
    t1 = href => {
      const currentPath = router.asPath.split("?")[0];
      if (href === "/" || href === orgRoot) {
        return currentPath === "/" || currentPath === orgRoot;
      }
      return currentPath.startsWith(href);
    };
    $[2] = orgRoot;
    $[3] = router.asPath;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isActive = t1;
  const queryClient = useQueryClient();
  const api = useApi();
  const [startDate] = useAtom(recentStartDateAtom);
  const [endDate] = useAtom(recentEndDateAtom);
  let t2;
  if ($[5] !== activeNamedOrg || $[6] !== api || $[7] !== endDate || $[8] !== queryClient || $[9] !== startDate) {
    t2 = () => {
      if (!activeNamedOrg || !startDate || !endDate) {
        return;
      }
      const logsQueryKey = ["logSearch", activeNamedOrg?.orgName || "", startDate.toISOString(), endDate.toISOString(), ""];
      const histogramQueryKey = ["logSearchHistogram", activeNamedOrg, "", startDate.toISOString(), endDate.toISOString()];
      const existingLogsData = queryClient.getQueryData(logsQueryKey);
      if (!existingLogsData) {
        queryClient.prefetchQuery(logsQueryKey, async () => {
          const response = await fetchLogSearch(api, activeNamedOrg, {
            from: startDate,
            to: endDate
          }, "");
          return response.logs.map(_temp2);
        }, {
          staleTime: 300000
        });
      }
      const existingHistogramData = queryClient.getQueryData(histogramQueryKey);
      if (!existingHistogramData) {
        queryClient.prefetchQuery(histogramQueryKey, () => fetchLogSearchHistogram(api, activeNamedOrg, "", {
          from: startDate,
          to: endDate
        }), {
          staleTime: 300000
        });
      }
    };
    $[5] = activeNamedOrg;
    $[6] = api;
    $[7] = endDate;
    $[8] = queryClient;
    $[9] = startDate;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const prefetchLogs = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <History className="size-[18px]" />;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== orgHref) {
    t4 = orgHref("/runs/jobs");
    $[12] = orgHref;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== isActive || $[15] !== orgHref || $[16] !== orgRoot) {
    t5 = isActive("/runs/jobs") || isActive(orgHref("/runs/jobs")) || isActive("/") || isActive(orgRoot ?? "/");
    $[14] = isActive;
    $[15] = orgHref;
    $[16] = orgRoot;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CirclePlay className="size-4" />;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t5) {
    t7 = {
      name: "Jobs",
      href: t4,
      current: t5,
      icon: t6
    };
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== orgHref) {
    t8 = orgHref("/runs/workflows");
    $[22] = orgHref;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== isActive || $[25] !== orgHref) {
    t9 = isActive("/runs/workflows") || isActive(orgHref("/runs/workflows"));
    $[24] = isActive;
    $[25] = orgHref;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Workflow className="size-4" />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t8 || $[29] !== t9) {
    t11 = {
      name: "Workflows",
      href: t8,
      current: t9,
      icon: t10
    };
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t7) {
    t12 = [t7, t11];
    $[31] = t11;
    $[32] = t7;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = t12 as NavSubItem[];
  let t14;
  if ($[34] !== t13) {
    t14 = {
      name: "Run History",
      isHoverCard: true,
      icon: t3,
      subItems: t13
    };
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== orgHref) {
    t15 = orgHref("/monitors");
    $[36] = orgHref;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== isActive || $[39] !== orgHref) {
    t16 = isActive("/monitors") || isActive(orgHref("/monitors"));
    $[38] = isActive;
    $[39] = orgHref;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Activity className="size-[18px]" />;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t15 || $[43] !== t16) {
    t18 = {
      name: "Monitors",
      href: t15,
      current: t16,
      icon: t17
    };
    $[42] = t15;
    $[43] = t16;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== orgHref) {
    t19 = orgHref("/rightsize");
    $[45] = orgHref;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== isActive || $[48] !== orgHref) {
    t20 = isActive("/rightsize") || isActive(orgHref("/rightsize"));
    $[47] = isActive;
    $[48] = orgHref;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Ruler className="size-[18px]" />;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t19 || $[52] !== t20) {
    t22 = {
      name: "Rightsize Runners",
      href: t19,
      current: t20,
      icon: t21
    };
    $[51] = t19;
    $[52] = t20;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== orgHref) {
    t23 = orgHref("/machine-definitions");
    $[54] = orgHref;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== isActive || $[57] !== orgHref) {
    t24 = isActive("/machine-definitions") || isActive(orgHref("/machine-definitions"));
    $[56] = isActive;
    $[57] = orgHref;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Layers className="size-[18px]" />;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t23 || $[61] !== t24) {
    t26 = {
      name: "Machine Definitions",
      href: t23,
      current: t24,
      icon: t25
    };
    $[60] = t23;
    $[61] = t24;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== orgHref) {
    t27 = orgHref("/logs");
    $[63] = orgHref;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== isActive || $[66] !== orgHref) {
    t28 = isActive("/logs") || isActive(orgHref("/logs"));
    $[65] = isActive;
    $[66] = orgHref;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Terminal className="size-[18px]" />;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== prefetchLogs || $[70] !== t27 || $[71] !== t28) {
    t30 = {
      name: "Logs",
      href: t27,
      current: t28,
      icon: t29,
      onMouseEnter: prefetchLogs
    };
    $[69] = prefetchLogs;
    $[70] = t27;
    $[71] = t28;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== globalNetworkMonitoringEnabled || $[74] !== isActive || $[75] !== orgHref) {
    t31 = globalNetworkMonitoringEnabled ? [{
      name: "Network Monitoring",
      href: orgHref("/network-monitoring"),
      current: isActive("/network-monitoring") || isActive(orgHref("/network-monitoring")),
      icon: <Network className="size-[18px]" />
    }] : [];
    $[73] = globalNetworkMonitoringEnabled;
    $[74] = isActive;
    $[75] = orgHref;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <GaugeCircleIcon className="size-[18px]" />;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== orgHref) {
    t33 = orgHref("/analytics");
    $[78] = orgHref;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== isActive || $[81] !== orgHref) {
    t34 = isActive("/analytics") || isActive(orgHref("/analytics"));
    $[80] = isActive;
    $[81] = orgHref;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <GaugeCircleIcon className="size-4" />;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t33 || $[85] !== t34) {
    t36 = {
      name: "Actions Analytics",
      href: t33,
      current: t34,
      icon: t35
    };
    $[84] = t33;
    $[85] = t34;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== orgHref) {
    t37 = orgHref("/docker-analytics");
    $[87] = orgHref;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== isActive || $[90] !== orgHref) {
    t38 = isActive("/docker-analytics") || isActive(orgHref("/docker-analytics"));
    $[89] = isActive;
    $[90] = orgHref;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <LiDockerIcon className="size-4 shrink-0" />;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== t37 || $[94] !== t38) {
    t40 = {
      name: "Docker Analytics",
      href: t37,
      current: t38,
      icon: t39
    };
    $[93] = t37;
    $[94] = t38;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  let t41;
  if ($[96] !== t36 || $[97] !== t40) {
    t41 = [t36, t40];
    $[96] = t36;
    $[97] = t40;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  const t42 = t41 as NavSubItem[];
  let t43;
  if ($[99] !== t42) {
    t43 = {
      name: "Analytics",
      isHoverCard: true,
      icon: t32,
      subItems: t42
    };
    $[99] = t42;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <HardDrive className="size-[18px]" />;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== orgHref) {
    t45 = orgHref("/stickydisks");
    $[102] = orgHref;
    $[103] = t45;
  } else {
    t45 = $[103];
  }
  let t46;
  if ($[104] !== isActive || $[105] !== orgHref || $[106] !== router.pathname) {
    t46 = isActive("/stickydisks") || isActive(orgHref("/stickydisks")) || router.pathname.startsWith(orgHref("/stickydisks"));
    $[104] = isActive;
    $[105] = orgHref;
    $[106] = router.pathname;
    $[107] = t46;
  } else {
    t46 = $[107];
  }
  let t47;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <HardDrive className="size-4" />;
    $[108] = t47;
  } else {
    t47 = $[108];
  }
  let t48;
  if ($[109] !== t45 || $[110] !== t46) {
    t48 = {
      name: "Sticky Disks",
      href: t45,
      current: t46,
      icon: t47
    };
    $[109] = t45;
    $[110] = t46;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] !== orgHref) {
    t49 = orgHref("/cache");
    $[112] = orgHref;
    $[113] = t49;
  } else {
    t49 = $[113];
  }
  let t50;
  if ($[114] !== isActive || $[115] !== orgHref || $[116] !== router.pathname) {
    t50 = isActive("/cache") || isActive(orgHref("/cache")) || router.pathname.startsWith("/cache/");
    $[114] = isActive;
    $[115] = orgHref;
    $[116] = router.pathname;
    $[117] = t50;
  } else {
    t50 = $[117];
  }
  let t51;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <DatabaseZap className="size-4" />;
    $[118] = t51;
  } else {
    t51 = $[118];
  }
  let t52;
  if ($[119] !== t49 || $[120] !== t50) {
    t52 = {
      name: "Cache",
      href: t49,
      current: t50,
      icon: t51
    };
    $[119] = t49;
    $[120] = t50;
    $[121] = t52;
  } else {
    t52 = $[121];
  }
  let t53;
  if ($[122] !== t48 || $[123] !== t52) {
    t53 = [t48, t52];
    $[122] = t48;
    $[123] = t52;
    $[124] = t53;
  } else {
    t53 = $[124];
  }
  const t54 = t53 as NavSubItem[];
  let t55;
  if ($[125] !== t54) {
    t55 = {
      name: "Storage",
      isHoverCard: true,
      icon: t44,
      subItems: t54
    };
    $[125] = t54;
    $[126] = t55;
  } else {
    t55 = $[126];
  }
  let t56;
  if ($[127] !== t14 || $[128] !== t18 || $[129] !== t22 || $[130] !== t26 || $[131] !== t30 || $[132] !== t31 || $[133] !== t43 || $[134] !== t55) {
    t56 = [t14, t18, t22, t26, t30, ...t31, t43, t55];
    $[127] = t14;
    $[128] = t18;
    $[129] = t22;
    $[130] = t26;
    $[131] = t30;
    $[132] = t31;
    $[133] = t43;
    $[134] = t55;
    $[135] = t56;
  } else {
    t56 = $[135];
  }
  const MainNav = t56;
  let t57;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
    $[136] = t57;
  } else {
    t57 = $[136];
  }
  let t58;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="GitHub Actions" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
    $[137] = t58;
  } else {
    t58 = $[137];
  }
  const t59 = redesign ? "pt-0" : "pt-[calc(var(--spacing-app-header-gap)-0.5rem)]";
  let t60;
  if ($[138] !== t59) {
    t60 = cn("border-0 min-h-0 flex-1 overflow-y-auto group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1", t59);
    $[138] = t59;
    $[139] = t60;
  } else {
    t60 = $[139];
  }
  let t61;
  if ($[140] !== navGapClass) {
    t61 = cn("flex flex-col group-data-[state=collapsed]:w-full", navGapClass);
    $[140] = navGapClass;
    $[141] = t61;
  } else {
    t61 = $[141];
  }
  let t62;
  if ($[142] !== navGapClass) {
    t62 = cn("flex flex-col", navGapClass);
    $[142] = navGapClass;
    $[143] = t62;
  } else {
    t62 = $[143];
  }
  const t63 = rowFillInsetClass && "[.group[data-state=expanded]_&_button]:border-y-2 [.group[data-state=expanded]_&_button]:bg-clip-padding";
  let t64;
  if ($[144] !== t63) {
    t64 = cn(t63);
    $[144] = t63;
    $[145] = t64;
  } else {
    t64 = $[145];
  }
  let t65;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <LazyMigrationWizard />;
    $[146] = t65;
  } else {
    t65 = $[146];
  }
  let t66;
  if ($[147] !== t64) {
    t66 = <div className="-mx-1 group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full relative"><MigrationWizardPopover><div className={t64}>{t65}</div></MigrationWizardPopover></div>;
    $[147] = t64;
    $[148] = t66;
  } else {
    t66 = $[148];
  }
  let t67;
  if ($[149] !== navGapClass) {
    t67 = cn("flex flex-col", navGapClass);
    $[149] = navGapClass;
    $[150] = t67;
  } else {
    t67 = $[150];
  }
  let t68;
  if ($[151] !== MainNav || $[152] !== prefetchOnIntent || $[153] !== rowFillInsetClass) {
    t68 = MainNav.map(item => item ? item.isHoverCard ? <SidebarHoverCardMenuItem key={item.name} name={item.name} icon={item.icon} subItems={item.subItems} prefetchOnIntent={prefetchOnIntent} buttonClassName={rowFillInsetClass} /> : <SidebarMenuItem key={item.name} className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1"><SidebarMenuButton asChild={true} isActive={item.current} tooltip={item.name} className={cn("h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer", rowFillInsetClass)}><RoutePrefetchLink href={item.href || "#"} prefetchOnIntent={prefetchOnIntent} className="cursor-pointer" {..."target" in item && item.target ? {
          target: item.target as string
        } : {}} {..."onClick" in item && item.onClick ? {
          onClick: item.onClick as () => void
        } : {}} {..."onMouseEnter" in item && item.onMouseEnter ? {
          onMouseEnter: item.onMouseEnter as () => void
        } : {}}><div className="relative">{item.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item.name}</span>{"isExternalLink" in item && item.isExternalLink ? <ExternalLink className="size-3.5 ml-1 opacity-0 -translate-x-1 group-hover/external:opacity-100 group-hover/external:translate-x-0 transition-all duration-200 text-neutral-500 dark:text-neutral-400 group-data-[state=collapsed]:hidden" /> : null}{"badge" in item && item.badge ? <div className="ml-auto pl-2 group-data-[state=collapsed]:hidden">{item.badge as React.ReactNode}</div> : null}</RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem> : null);
    $[151] = MainNav;
    $[152] = prefetchOnIntent;
    $[153] = rowFillInsetClass;
    $[154] = t68;
  } else {
    t68 = $[154];
  }
  let t69;
  if ($[155] !== t67 || $[156] !== t68) {
    t69 = <SidebarMenu className={t67}>{t68}</SidebarMenu>;
    $[155] = t67;
    $[156] = t68;
    $[157] = t69;
  } else {
    t69 = $[157];
  }
  let t70;
  if ($[158] !== t62 || $[159] !== t66 || $[160] !== t69) {
    t70 = <div className={t62}>{t66}{t69}</div>;
    $[158] = t62;
    $[159] = t66;
    $[160] = t69;
    $[161] = t70;
  } else {
    t70 = $[161];
  }
  let t71;
  if ($[162] !== t61 || $[163] !== t70) {
    t71 = <SidebarGroupContent className="flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><div className={t61}>{t70}</div></SidebarGroupContent>;
    $[162] = t61;
    $[163] = t70;
    $[164] = t71;
  } else {
    t71 = $[164];
  }
  let t72;
  if ($[165] !== t60 || $[166] !== t71) {
    t72 = <SidebarContent className="flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden"><SidebarGroup className={t60}>{t71}</SidebarGroup></SidebarContent>;
    $[165] = t60;
    $[166] = t71;
    $[167] = t72;
  } else {
    t72 = $[167];
  }
  let t73;
  let t74;
  if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0" />;
    t74 = <SidebarRail />;
    $[168] = t73;
    $[169] = t74;
  } else {
    t73 = $[168];
    t74 = $[169];
  }
  let t75;
  if ($[170] !== t72) {
    t75 = <><ShadcnSidebar collapsible="icon" className={t57}>{t58}{t72}{t73}{t74}</ShadcnSidebar></>;
    $[170] = t72;
    $[171] = t75;
  } else {
    t75 = $[171];
  }
  return t75;
}
function _temp2(log) {
  return {
    ...log
  };
}